/**
 * The drill's keyboard map.
 *
 * The goal is a whole session run from the numeric keypad, without the mouse:
 *
 *   0   flip the card
 *   1   missed it        2   got it
 *   3   replay the audio  (also Enter and the decimal point)
 *
 * Two details make this fiddlier than a `switch` on `event.key`.
 *
 * **Num Lock.** With it off, the keypad sends navigation keys — numpad 0 is
 * `Insert`, 1 is `End`, 2 is `ArrowDown`, 3 is `PageDown`, and the decimal
 * point is `Delete`. `event.code` is unaffected (`Numpad0`, `Numpad1`, …), so
 * matching on the code is what makes the pad work either way. Someone drilling
 * vocabulary should not have to notice which state their keyboard is in.
 *
 * **The top row.** `Digit0`–`Digit3` are matched too, so a laptop without a
 * keypad gets the same bindings, and space still flips because that is what
 * hands expect.
 *
 * Kept as a pure function of (event, flipped) so every binding — including the
 * Num Lock ones, which are tedious to trigger by hand — is unit-testable.
 */

export type DrillAction = 'flip' | 'correct' | 'incorrect' | 'replay';

/** The subset of KeyboardEvent this needs; keeps the tests free of DOM stubs. */
export interface KeyLike {
  key: string;
  code: string;
}

/** Physical keys, reliable whether or not Num Lock is on. */
const BY_CODE: Record<string, DrillAction> = {
  Space: 'flip',
  Numpad0: 'flip',
  Digit0: 'flip',

  Numpad1: 'incorrect',
  Digit1: 'incorrect',

  Numpad2: 'correct',
  Digit2: 'correct',

  Numpad3: 'replay',
  Digit3: 'replay',

  NumpadDecimal: 'replay',
  Period: 'replay',
  NumpadEnter: 'replay',
  Enter: 'replay',
};

/**
 * Fallbacks for keys with no useful code, and for browsers that do not report
 * one. Arrows are here rather than in `BY_CODE` because with Num Lock off the
 * keypad's own arrows carry `Numpad4`/`Numpad6` codes, which are already spoken
 * for by intent rather than by position.
 */
const BY_KEY: Record<string, DrillAction> = {
  ' ': 'flip',
  '0': 'flip',
  '1': 'incorrect',
  '2': 'correct',
  '3': 'replay',
  '.': 'replay',
  ',': 'replay', // some layouts put a comma on the keypad's decimal key
  Enter: 'replay',
  ArrowLeft: 'incorrect',
  ArrowRight: 'correct',
};

/**
 * What this keypress should do, or `null` for keys the drill ignores.
 *
 * Grading is refused while the card is face down — answering a card you have
 * not seen the back of is a mis-key, not an intention — and flipping is refused
 * once it is already face up, so a stray repeat cannot skip a card.
 */
export function drillActionFor(event: KeyLike, flipped: boolean): DrillAction | null {
  const action = BY_CODE[event.code] ?? BY_KEY[event.key] ?? null;
  if (action === null) return null;

  if (action === 'flip') return flipped ? null : 'flip';
  if (action === 'correct' || action === 'incorrect') return flipped ? action : null;
  return action; // replay works on either side
}

/** True for keys the drill consumes, so the caller can preventDefault. */
export function isDrillKey(event: KeyLike, flipped: boolean): boolean {
  return drillActionFor(event, flipped) !== null;
}
